import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { colors2, ButtonGhostCompact } from "oolib";
import { UI_BODY_SM, UI_BODY_SEMIBOLD_SM_DF, UI_CAPTION, UI_CAPTION_DF, UI_CAPTION_SEMIBOLD_DF } from "oolib/dist/v2/components/Typo2";
import { Hash } from "@phosphor-icons/react";
import { DashboardWidgetShell } from "@/_self_managed/SM_Dashboard/comps/DashboardWidgetShell";
import { SentimentBar } from "@/_self_managed/SM_Dashboard/LensesV2/comps/SentimentBar";
import { useSignalCategories } from "@/components/analysisModules/TopicClusteringTreeView/SignalCategoriesContext";
import { toneColor } from "@/components/analysisModules/TopicClusteringTreeView/PrioritiesLayout/format";

/**
 * Topics, largest first: a row per topic with its observations as a bar
 * (the largest topic fills the track, the rest scale to it) split by signal
 * category, the move in its share of all observations since the comparison
 * period, and how many of its sub-topics are fires. All from what the
 * Priorities scoring already holds:
 *   - the counts tree: each topic's observations and their category split;
 *   - the sub-topic series: a topic's count per period is its sub-topics'
 *     plus its parked observations, which with the scorer's period totals
 *     gives its share then and now;
 *   - the scored items: fires per topic.
 */
const TOP_N = 10;
// A row opens the topic's landing page in a side panel (onOpenTopic), so
// it reads as clickable: pointer, and a hover tint that bleeds past the
// shell padding.
const Row = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.4fr) minmax(0, 1fr) 84px 64px;
  column-gap: 20px;
  align-items: center;
  padding: 12px 8px;
  margin: 0 -8px;
  border-radius: 8px;
  border-bottom: 1px solid ${colors2.grey5};
  cursor: pointer;

  &:hover {
    background: ${colors2.grey2};
  }

  &:last-of-type {
    border-bottom: none;
  }
`;
const Track = styled.div`
  width: 100%;
  height: 8px;
  border-radius: 4px;
  background: ${colors2.grey5};
  overflow: hidden;
`;
const Foot = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding-top: 6px;
`;

const sumAt = (series, key) => (series ? series[key] || 0 : 0);
const signed = (n) => `${n > 0 ? "+" : n < 0 ? "−" : ""}${Math.abs(n).toFixed(1)}`;
// A move under half a point reads grey: not worth a colour.
const deltaColor = (d) => (Math.abs(d) < 0.5 ? colors2.grey50 : toneColor(d > 0 ? "up" : "down"));

export const TopicSharesWidget = ({ counts, seriesByNode, scored, onOpenTopic }) => {
  const navigate = useNavigate();
  const categoryTheme = useSignalCategories();
  const { priorities, wins, meta } = scored;

  const rows = useMemo(() => {
    const items = [...priorities, ...wins];
    const topics = (counts?.tree || []).filter((t) => t.annotationCount > 0);
    const maxCount = topics.reduce((m, t) => Math.max(m, t.annotationCount || 0), 0);
    const totalNow = meta.totalsByKey?.[meta.nowKey] || 0;
    const totalThen = meta.compareKey ? meta.totalsByKey?.[meta.compareKey] || 0 : 0;
    return topics
      .sort((a, b) => (b.annotationCount || 0) - (a.annotationCount || 0))
      .slice(0, TOP_N)
      .map((topic) => {
        const nodeKeys = [...(topic.children || []).map((c) => String(c.nodeId)), `__unplaced:${topic.nodeId}`];
        const now = nodeKeys.reduce((acc, k) => acc + sumAt(seriesByNode[k], meta.nowKey), 0);
        const then = meta.compareKey ? nodeKeys.reduce((acc, k) => acc + sumAt(seriesByNode[k], meta.compareKey), 0) : 0;
        const delta = totalNow && totalThen ? (now / totalNow - then / totalThen) * 100 : null;
        const split = topic.signalCategoryCounts || {};
        const splitTotal = Object.values(split).reduce((a, b) => a + b, 0);
        const segments = categoryTheme.list
          .map((cat) => ({ key: cat.value, title: cat.display, value: splitTotal ? Math.round(((split[cat.value] || 0) / splitTotal) * 100) : 0, color: categoryTheme.accentOf(cat.value) }))
          .filter((seg) => seg.value > 0);
        return {
          nodeId: topic.nodeId,
          label: topic.label,
          width: maxCount ? ((topic.annotationCount || 0) / maxCount) * 100 : 0,
          segments,
          delta,
          fires: items.filter((item) => item.bucketLabel === topic.label && item.zone === "fires").length,
        };
      });
  }, [counts, seriesByNode, priorities, wins, meta, categoryTheme]);
  const topicCount = (counts?.tree || []).filter((t) => t.annotationCount > 0).length;

  return (
    <DashboardWidgetShell
      titleJSX={
        <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", minWidth: 0 }}>
          <Hash size={16} color={colors2.grey80} weight="bold" />
          <div style={{ display: "flex", alignItems: "baseline", gap: "0.8rem", minWidth: 0 }}>
            <UI_BODY_SEMIBOLD_SM_DF>Topics</UI_BODY_SEMIBOLD_SM_DF>
            <UI_CAPTION style={{ color: colors2.grey60 }}>Largest first. Bar is share of observations, split by signal category.</UI_CAPTION>
          </div>
        </div>
      }
      style={{ gap: 0 }}
    >
      {rows.map((row) => (
        <Row key={String(row.nodeId)} role="button" onClick={() => onOpenTopic(row.nodeId)}>
          <UI_BODY_SM style={{ color: colors2.grey80, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{row.label}</UI_BODY_SM>
          <Track>
            <div style={{ width: `${row.width}%` }}>
              <SentimentBar segments={row.segments} height={8} roundedEnds />
            </div>
          </Track>
          <UI_CAPTION_SEMIBOLD_DF style={{ color: row.delta === null ? colors2.grey50 : deltaColor(row.delta), textAlign: "right", whiteSpace: "nowrap" }}>
            {row.delta === null ? "–" : `${signed(row.delta)} pts`}
          </UI_CAPTION_SEMIBOLD_DF>
          <UI_CAPTION_DF style={{ color: colors2.grey40, textAlign: "right", whiteSpace: "nowrap" }}>
            {row.fires} fire{row.fires === 1 ? "" : "s"}
          </UI_CAPTION_DF>
        </Row>
      ))}
      <Foot>
        <UI_CAPTION style={{ color: colors2.grey40 }}>
          {meta.prevLabel ? `Δ = share change ${meta.prevLabel} → ${meta.nowLabel}` : "Δ = share change; no earlier period to compare with"}
        </UI_CAPTION>
        <ButtonGhostCompact
          S
          value={`All ${topicCount} topics`}
          iconAfter="ArrowRight"
          onClick={() => navigate("/topics")}
        />
      </Foot>
    </DashboardWidgetShell>
  );
};
